import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { 
  Gem, TrendingUp, TrendingDown, BarChart3, AlertTriangle, Target, FileText,
  ArrowUpRight, ArrowDownRight, Info, Factory, Layers, Package
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import PageHeader from '../../components/PageHeader';
import KPICardOperations from '../../components/KPICardOperations';
import RealVsBudgetTrajectory from '../../components/RealVsBudgetTrajectory';
import { productionRevenueTrajectoryKpis, productionRevenueData } from '../../data/mock/operationsData';
import { processKeyMessages } from '../../utils/iconMapping';

// ============ COMPONENTS ============

// Status Badge Component
const StatusBadge = ({ status, language }) => {
  const configs = {
    'conforme': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: { pt: 'Conforme', en: 'On Track' } },
    'acompanhamento': { bg: 'bg-blue-100', text: 'text-blue-700', label: { pt: 'Acompanhamento', en: 'Monitoring' } },
    'pressao': { bg: 'bg-amber-100', text: 'text-amber-700', label: { pt: 'Pressão', en: 'Pressure' } },
  };
  const config = configs[status] || configs['conforme'];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-semibold ${config.bg} ${config.text}`}>
      {config.label[language]}
    </span>
  );
};

// Quality Badge Component
const QualityBadge = ({ quality, language }) => {
  const configs = {
    'alto': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: { pt: 'Alto', en: 'High' } },
    'médio': { bg: 'bg-blue-100', text: 'text-blue-700', label: { pt: 'Médio', en: 'Medium' } },
    'baixo': { bg: 'bg-amber-100', text: 'text-amber-700', label: { pt: 'Baixo', en: 'Low' } },
    'variável': { bg: 'bg-slate-100', text: 'text-slate-700', label: { pt: 'Variável', en: 'Variable' } },
  };
  const config = configs[quality] || configs['médio'];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-semibold ${config.bg} ${config.text}`}>
      {config.label[language]}
    </span>
  );
};

// Severity Badge Component
const SeverityBadge = ({ severity, language }) => {
  const configs = {
    'high': { bg: 'bg-rose-100', text: 'text-rose-700', label: { pt: 'Relevante', en: 'Relevant' } },
    'medium': { bg: 'bg-amber-100', text: 'text-amber-700', label: { pt: 'Moderado', en: 'Moderate' } },
    'low': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: { pt: 'Limitado', en: 'Limited' } },
  };
  const config = configs[severity] || configs['medium'];
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold ${config.bg} ${config.text}`}>
      {config.label[language]}
    </span>
  );
};

// ============ MAIN COMPONENT ============
const ProductionRevenue = ({ year, setActiveSection, flowMap }) => {
  const { language } = useLanguage();
  const is2026 = year === 2026;
  const rawData = is2026 ? productionRevenueData[2026] : productionRevenueData[2025];
  
  // Process data: map icon keys to actual icon components
  const data = {
    ...rawData,
    keyMessages: processKeyMessages(rawData.keyMessages),
  };

  // Dados para o painel Real vs Trajectória do Orçamento
  const trajectoryKpisYear = is2026 ? productionRevenueTrajectoryKpis[2026] : productionRevenueTrajectoryKpis[2025];

  // Preparar dados para export PDF
  const pageDataForExport = {
    kpis: data.kpis,
    kpiLabels: {
      quilatesRecuperados: language === 'pt' ? 'Quilates Recuperados' : 'Recovered Carats',
      teor: language === 'pt' ? 'Teor' : 'Grade',
      tamanhoMedioPedra: language === 'pt' ? 'Tamanho Médio da Pedra' : 'Avg Stone Size',
      pedrasEspeciais: language === 'pt' ? 'N.º de Pedras Especiais' : 'Special Stones',
    },
    keyMessages: data.keyMessages,
    constrangimentos: data.constrangimentos,
    ponteFinanceira: data.ponteFinanceira,
  };

  return (
    <div className="space-y-6" data-testid="production-revenue">
      {/* Page Header */}
      <PageHeader
        title={language === 'pt' ? 'Produção e Receita' : 'Production & Revenue'}
        period={data.period}
        subtitle={language === 'pt' 
          ? `vs Orçamento ${data.period} e comparativo com ${is2026 ? '1T25' : '2024'}`
          : `vs Budget ${data.period} and comparison with ${is2026 ? 'Q1 25' : '2024'}`}
        language={language}
        flowMap={flowMap}
        pageData={pageDataForExport}
      />

      {/* ============ MENSAGENS-CHAVE - McKinsey Premium Compact ============ */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#112240] rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-0.5 h-4 bg-gold-500 rounded-full"></div>
          <h3 className="text-white/90 font-medium text-xs uppercase tracking-[0.15em]">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Messages'}
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {data.keyMessages.map((msg, idx) => {
            const MsgIcon = msg.icon;
            return (
              <div key={idx} className="bg-white/5 border border-white/10 rounded-lg p-3 hover:bg-white/8 transition-colors">
                <div className="flex items-start gap-2.5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${msg.iconBg}`}>
                    <MsgIcon className={`w-4 h-4 ${msg.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-semibold text-[10px] mb-1.5 uppercase tracking-wider ${msg.titleColor}`}>
                      {msg.title[language]}
                    </h4>
                    <p className="text-slate-300/90 text-[11px] leading-relaxed">{msg.text[language]}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ============ 4 KPIs OFICIAIS ============ */}
      <div className="grid grid-cols-4 gap-4">
        <KPICardOperations 
          label={language === 'pt' ? 'Quilates Recuperados' : 'Carats Recovered'}
          value={data.kpis.quilatesRecuperados.actual.toLocaleString()}
          unit={data.kpis.quilatesRecuperados.unit}
          vsBudget={data.kpis.quilatesRecuperados.variance}
          vsPrior={data.kpis.quilatesRecuperados.yoyVariance}
          budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
          yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Quilates Recuperados' : 'Carats Recovered',
            definition: language === 'pt' 
              ? 'Volume total de diamantes recuperados no período, em quilates.'
              : 'Total volume of diamonds recovered in the period, in carats.',
            relevance: language === 'pt'
              ? 'Principal output operacional. Determina directamente a capacidade de venda e geração de receita.'
              : 'Main operational output. Directly determines sales capacity and revenue generation.',
            currentReading: language === 'pt'
              ? `Recuperação de ${data.kpis.quilatesRecuperados.actual.toLocaleString()} quilates (${data.kpis.quilatesRecuperados.variance > 0 ? '+' : ''}${data.kpis.quilatesRecuperados.variance}% vs orçamento).`
              : `Recovery of ${data.kpis.quilatesRecuperados.actual.toLocaleString()} carats (${data.kpis.quilatesRecuperados.variance > 0 ? '+' : ''}${data.kpis.quilatesRecuperados.variance}% vs budget).`,
            drillDown: [
              { label: language === 'pt' ? 'Regulares' : 'Regular', value: '78.5K ct', variance: -5.2 },
              { label: language === 'pt' ? 'Especiais (+10.8ct)' : 'Special (+10.8ct)', value: '12.8K ct', variance: 8.5 },
              { label: language === 'pt' ? 'Rejeitados' : 'Rejected', value: '3.2K ct', variance: -12.0 },
            ]
          }}
        />
        <KPICardOperations 
          label={language === 'pt' ? 'Teor' : 'Grade'}
          value={data.kpis.teor.actual.toFixed(2)}
          unit={data.kpis.teor.unit}
          vsBudget={data.kpis.teor.variance}
          vsPrior={data.kpis.teor.yoyVariance}
          budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
          yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Teor de Recuperação' : 'Recovery Grade',
            definition: language === 'pt' 
              ? 'Quilates recuperados por 100 toneladas de minério tratado (cpht).'
              : 'Carats recovered per 100 tonnes of ore processed (cpht).',
            relevance: language === 'pt'
              ? 'Combina qualidade do minério com eficiência da planta. Teor baixo exige maior volume para metas de produção.'
              : 'Combines ore quality with plant efficiency. Low grade requires higher volume for production targets.',
            currentReading: language === 'pt'
              ? `Teor de ${data.kpis.teor.actual.toFixed(2)} cpht reflecte ${data.kpis.teor.variance >= 0 ? 'boa' : 'baixa'} qualidade do blend de alimentação.`
              : `Grade of ${data.kpis.teor.actual.toFixed(2)} cpht reflects ${data.kpis.teor.variance >= 0 ? 'good' : 'poor'} feed blend quality.`,
            drillDown: [
              { label: 'Mina 3', value: '17.2 cpht', variance: 3.5 },
              { label: 'Mina 4', value: '13.8 cpht', variance: -8.2 },
              { label: language === 'pt' ? 'Média Ponderada' : 'Weighted Avg', value: '15.74 cpht', variance: -2.2 },
            ]
          }}
        />
        <KPICardOperations 
          label={language === 'pt' ? 'Tamanho Médio da Pedra' : 'Avg Stone Size'}
          value={data.kpis.tamanhoMedioPedra.actual.toFixed(2)}
          unit={data.kpis.tamanhoMedioPedra.unit}
          vsBudget={data.kpis.tamanhoMedioPedra.variance}
          vsPrior={data.kpis.tamanhoMedioPedra.yoyVariance}
          budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
          yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Tamanho Médio da Pedra' : 'Avg Stone Size',
            definition: language === 'pt' 
              ? 'Peso médio por diamante recuperado, expresso em quilates por pedra.'
              : 'Average weight per recovered diamond, expressed in carats per stone.',
            relevance: language === 'pt'
              ? 'Driver crítico de preço. Pedras maiores têm prémio exponencial no mercado de diamantes.'
              : 'Critical price driver. Larger stones command exponential premium in diamond market.',
            currentReading: language === 'pt'
              ? `Tamanho médio de ${data.kpis.tamanhoMedioPedra.actual.toFixed(2)} ct/pedra ${data.kpis.tamanhoMedioPedra.variance >= 0 ? 'suporta' : 'pressiona'} o preço médio.`
              : `Average size of ${data.kpis.tamanhoMedioPedra.actual.toFixed(2)} ct/stone ${data.kpis.tamanhoMedioPedra.variance >= 0 ? 'supports' : 'pressures'} average price.`,
            drillDown: [
              { label: language === 'pt' ? 'Categoria Gem' : 'Gem Category', value: '0.52 ct/pedra' },
              { label: language === 'pt' ? 'Categoria Industrial' : 'Industrial Category', value: '0.18 ct/pedra' },
              { label: language === 'pt' ? 'Mix Total' : 'Total Mix', value: '0.42 ct/pedra' },
            ]
          }}
        />
        <KPICardOperations 
          label={language === 'pt' ? 'N.º de Pedras Especiais' : 'Special Stones'}
          value={data.kpis.pedrasEspeciais.actual}
          unit={data.kpis.pedrasEspeciais.unit}
          vsBudget={data.kpis.pedrasEspeciais.variance}
          vsPrior={data.kpis.pedrasEspeciais.yoyVariance}
          budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
          yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Pedras Especiais' : 'Special Stones',
            definition: language === 'pt' 
              ? 'Diamantes de alta qualidade ou tamanho excepcional (+10.8 ct ou qualidade fancy color).'
              : 'High quality or exceptional size diamonds (+10.8 ct or fancy color quality).',
            relevance: language === 'pt'
              ? 'Representam ~20-25% da receita total. Uma pedra >50ct pode valer mais que 1000 pedras regulares.'
              : 'Represent ~20-25% of total revenue. A single >50ct stone can be worth more than 1000 regular stones.',
            currentReading: language === 'pt'
              ? `${data.kpis.pedrasEspeciais.actual} pedras especiais recuperadas, contribuindo significativamente para o preço médio.`
              : `${data.kpis.pedrasEspeciais.actual} special stones recovered, significantly contributing to average price.`,
            drillDown: [
              { label: language === 'pt' ? '+50ct (raras)' : '+50ct (rare)', value: '2 pedras', variance: 100 },
              { label: language === 'pt' ? '20-50ct' : '20-50ct', value: '9 pedras', variance: 12.5 },
              { label: language === 'pt' ? '10.8-20ct' : '10.8-20ct', value: '27 pedras', variance: -6.8 },
            ]
          }}
        />
      </div>

      {/* ============ REAL VS TRAJECTÓRIA DO ORÇAMENTO ============ */}
      <RealVsBudgetTrajectory 
        year={year}
        kpis={trajectoryKpisYear}
        defaultKpi="quilatesRecuperados"
      />

      {/* ============ SUMÁRIO EXECUTIVO - Formato Demonstração de Resultados ============ */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Sumário Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Produção e receita' : 'Production and revenue'}
        language={language}
        maxHeight="500px"
        icon={Gem}
        dataTestId="production-revenue-executive-summary"
      >
        {/* Executive Overview */}
        <div className="mb-6">
          <p className="text-slate-700 leading-relaxed text-sm mb-4">
            {language === 'pt' 
              ? `Esta página acompanha os drivers operacionais da receita. A leitura deve combinar volume, teor e qualidade do produto. O CFO deve perceber se o desvio está na quantidade produzida, na qualidade do produto ou no mix recuperado.`
              : `This page tracks the operational revenue drivers. The reading should combine volume, grade and product quality. The CFO should understand if the deviation is in quantity produced, product quality or recovered mix.`}
          </p>
          <p className="text-slate-700 leading-relaxed text-sm">
            {language === 'pt' 
              ? `O ${data.period} mostra quilates recuperados de ${data.kpis.quilatesRecuperados.actual.toLocaleString()} (${data.kpis.quilatesRecuperados.variance > 0 ? '+' : ''}${data.kpis.quilatesRecuperados.variance}% vs plano), teor de ${data.kpis.teor.actual} cpht e tamanho médio da pedra de ${data.kpis.tamanhoMedioPedra.actual} ct/pedra. A operação pode estar a cumprir volume e ainda assim pressionar receita, se o teor, o tamanho médio da pedra ou o número de especiais estiverem abaixo do esperado.`
              : `${data.period} shows recovered carats of ${data.kpis.quilatesRecuperados.actual.toLocaleString()} (${data.kpis.quilatesRecuperados.variance > 0 ? '+' : ''}${data.kpis.quilatesRecuperados.variance}% vs plan), grade of ${data.kpis.teor.actual} cpht and average stone size of ${data.kpis.tamanhoMedioPedra.actual} ct/stone. Operations may be meeting volume and still pressuring revenue, if grade, average stone size or number of specials are below expected.`}
          </p>
        </div>

        {/* Driver Sections */}
        <div className="space-y-4">
          {/* Produção Mensal e Conversão em Quilates */}
          <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-5 border border-blue-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Gem className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Produção e Conversão em Quilates' : 'Production and Carat Conversion'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Estamos a converter o esforço operacional em quilates ao ritmo esperado?' : 'Are we converting operational effort to carats at expected rate?'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">{data.producaoMensal.leituraExecutiva[language]}</p>
            <div className="bg-white/60 rounded-lg p-3 border border-blue-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Resumo do Período' : 'Period Summary'}</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">{language === 'pt' ? 'Quilates' : 'Carats'}</p>
                  <p className="text-sm font-bold text-slate-700">{data.producaoMensal.ytd.quilates.real.toLocaleString()} {data.producaoMensal.ytd.quilates.unit}</p>
                  <p className={`text-[10px] font-semibold ${data.producaoMensal.ytd.quilates.variance >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {data.producaoMensal.ytd.quilates.variance > 0 ? '+' : ''}{data.producaoMensal.ytd.quilates.variance}% vs orç.
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">{language === 'pt' ? 'Minério Tratado' : 'Ore Processed'}</p>
                  <p className="text-sm font-bold text-slate-700">{data.producaoMensal.ytd.minerioTratado.real} {data.producaoMensal.ytd.minerioTratado.unit}</p>
                  <p className={`text-[10px] font-semibold ${data.producaoMensal.ytd.minerioTratado.variance >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {data.producaoMensal.ytd.minerioTratado.variance > 0 ? '+' : ''}{data.producaoMensal.ytd.minerioTratado.variance}% vs orç.
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">{language === 'pt' ? 'Conversão (Teor)' : 'Conversion (Grade)'}</p>
                  <p className="text-sm font-bold text-slate-700">{data.producaoMensal.ytd.conversao.real} {data.producaoMensal.ytd.conversao.unit}</p>
                  <p className={`text-[10px] font-semibold ${data.producaoMensal.ytd.conversao.variance >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {data.producaoMensal.ytd.conversao.variance > 0 ? '+' : ''}{data.producaoMensal.ytd.conversao.variance}% vs orç.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Teor e Qualidade do Produto */}
          <div className="bg-gradient-to-r from-emerald-50 to-slate-50 rounded-xl p-5 border border-emerald-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Target className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Teor e Qualidade do Produto' : 'Grade and Product Quality'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'A qualidade da produção está a proteger ou a pressionar o potencial de receita?' : 'Is production quality protecting or pressuring revenue potential?'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">{data.qualidadeProduto.leituraQualidade?.[language] || data.producaoMensal.leituraExecutiva[language]}</p>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="bg-white/60 rounded-lg p-3 border border-emerald-100 text-center">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">{language === 'pt' ? 'Teor' : 'Grade'}</p>
                <p className="text-lg font-bold text-slate-700">{data.qualidadeProduto.teor.actual}</p>
                <p className={`text-[10px] font-semibold ${data.qualidadeProduto.teor.variance >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {data.qualidadeProduto.teor.variance > 0 ? '+' : ''}{data.qualidadeProduto.teor.variance}%
                </p>
              </div>
              <div className="bg-white/60 rounded-lg p-3 border border-emerald-100 text-center">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">{language === 'pt' ? 'Tam. Médio' : 'Avg Size'}</p>
                <p className="text-lg font-bold text-slate-700">{data.qualidadeProduto.tamanhoMedio.actual}</p>
                <p className={`text-[10px] font-semibold ${data.qualidadeProduto.tamanhoMedio.variance >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {data.qualidadeProduto.tamanhoMedio.variance > 0 ? '+' : ''}{data.qualidadeProduto.tamanhoMedio.variance}%
                </p>
              </div>
              <div className="bg-white/60 rounded-lg p-3 border border-emerald-100 text-center">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">{language === 'pt' ? 'Especiais' : 'Specials'}</p>
                <p className="text-lg font-bold text-slate-700">{data.qualidadeProduto.especiais.actual}</p>
                <p className={`text-[10px] font-semibold ${data.qualidadeProduto.especiais.variance >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {data.qualidadeProduto.especiais.variance > 0 ? '+' : ''}{data.qualidadeProduto.especiais.variance}%
                </p>
              </div>
            </div>
            {data.qualidadeProduto.sfdReading && (
              <p className="text-[10px] text-slate-500 flex items-center gap-1">
                <Info className="w-3 h-3" />
                {data.qualidadeProduto.sfdReading[language]}
              </p>
            )}
          </div>

          {/* Mix Operacional - Only for 2026 */}
          {is2026 && data.mixOperacional && (
            <div className="bg-gradient-to-r from-amber-50 to-slate-50 rounded-xl p-5 border border-amber-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Layers className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Mix Operacional com Impacto Comercial' : 'Operational Mix with Commercial Impact'}</h4>
                  <p className="text-xs text-slate-500">{language === 'pt' ? 'O mix de produção ajuda a explicar o comportamento da qualidade?' : 'Does production mix help explain quality behavior?'}</p>
                </div>
              </div>
              <p className="text-slate-600 text-xs leading-relaxed mb-3">{data.mixReading[language]}</p>
            </div>
          )}
        </div>
      </ExecutiveSummarySection>

      {/* ============ BLOCO 1: PRODUÇÃO MENSAL - 2 Gráficos ============ */}
      {is2026 && data.producaoMensal?.monthly && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <BarChart3 className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 text-sm">{language === 'pt' ? 'Produção Mensal e Conversão' : 'Monthly Production and Conversion'}</h3>
              <p className="text-[10px] text-slate-500">{language === 'pt' ? 'Quilates recuperados vs plano e minério tratado como contexto' : 'Carats recovered vs plan and ore processed as context'}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-6">
            {/* Visual A — Quilates Recuperados vs Plano */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-slate-700 text-xs">{language === 'pt' ? 'Quilates Recuperados vs Plano' : 'Carats Recovered vs Plan'}</h4>
                  <p className="text-[10px] text-slate-400">{language === 'pt' ? 'Output convertido em quilates' : 'Output converted to carats'}</p>
                </div>
                <div className="flex items-center gap-3 text-[10px]">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-gold-500"></div>
                    <span className="text-slate-600">{language === 'pt' ? 'Real' : 'Actual'}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-gold-500/30 border border-gold-400/50"></div>
                    <span className="text-slate-600">{language === 'pt' ? 'Orçamento' : 'Budget'}</span>
                  </div>
                </div>
              </div>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.producaoMensal.monthly} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 10 }} />
                    <YAxis 
                      tick={{ fill: '#64748b', fontSize: 10 }} 
                      tickFormatter={(v) => `${(v/1000).toFixed(1)}K`}
                      label={{ value: 'cts', angle: -90, position: 'insideLeft', style: { fontSize: 9, fill: '#94a3b8' } }}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#0A192F', border: 'none', borderRadius: '8px', fontSize: '11px' }}
                      labelStyle={{ color: '#C5A059' }}
                      formatter={(value) => [`${value.toLocaleString()} cts`, '']}
                    />
                    <Bar dataKey="quilatesReal" name={language === 'pt' ? 'Quilates Real' : 'Actual Carats'} fill="#C5A059" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="quilatesOrc" name={language === 'pt' ? 'Quilates Orçamento' : 'Budget Carats'} fill="#C5A05940" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* YTD Summary */}
              <div className="mt-3 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-500 font-medium">{language === 'pt' ? 'Acumulado Período:' : 'Period YTD:'}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-700">
                      {data.producaoMensal.ytd.quilates.real.toLocaleString()} vs {data.producaoMensal.ytd.quilates.orc.toLocaleString()} cts
                    </span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${data.producaoMensal.ytd.quilates.variance >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                      {data.producaoMensal.ytd.quilates.variance > 0 ? '+' : ''}{data.producaoMensal.ytd.quilates.variance}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Visual B — Minério Tratado (Contexto) */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-slate-700 text-xs">{language === 'pt' ? 'Minério Tratado (Contexto)' : 'Ore Processed (Context)'}</h4>
                  <p className="text-[10px] text-slate-400">{language === 'pt' ? 'Volume processado pela planta' : 'Volume processed by plant'}</p>
                </div>
                <div className="flex items-center gap-1.5 text-[10px]">
                  <div className="w-3 h-3 rounded-sm bg-primary"></div>
                  <span className="text-slate-600">{language === 'pt' ? 'Tratado (K m³)' : 'Processed (K m³)'}</span>
                </div>
              </div>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.producaoMensal.monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 10 }} />
                    <YAxis 
                      tick={{ fill: '#64748b', fontSize: 10 }} 
                      tickFormatter={(v) => `${v}`}
                      label={{ value: "K m³", angle: -90, position: 'insideLeft', style: { fontSize: 9, fill: '#94a3b8' } }}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#0A192F', border: 'none', borderRadius: '8px', fontSize: '11px' }}
                      labelStyle={{ color: '#C5A059' }}
                      formatter={(value) => [`${value.toLocaleString()} K m³`, '']}
                    />
                    <Bar dataKey="minerioTratado" name={language === 'pt' ? 'Minério Tratado' : 'Ore Processed'} fill="#0A192F" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* YTD Summary */}
              <div className="mt-3 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-500 font-medium">{language === 'pt' ? 'Acumulado Período:' : 'Period YTD:'}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-700">
                      {data.producaoMensal.ytd.minerioTratado.real} vs {data.producaoMensal.ytd.minerioTratado.orc} K m³
                    </span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${data.producaoMensal.ytd.minerioTratado.variance >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                      {data.producaoMensal.ytd.minerioTratado.variance > 0 ? '+' : ''}{data.producaoMensal.ytd.minerioTratado.variance}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Reading Note */}
          <div className="mt-4 p-3 bg-primary/5 rounded-lg border border-primary/10">
            <p className="text-[11px] text-slate-600 flex items-start gap-2">
              <Info className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <span>
                <strong className="text-primary">{language === 'pt' ? 'Leitura:' : 'Reading:'}</strong>
                {language === 'pt' 
                  ? ' Comparar quilates recuperados vs plano (esquerda) e minério tratado (direita). Se o desvio dos quilates for maior que o desvio do minério, o problema está na conversão (teor). Se forem iguais, o problema está no volume tratado.'
                  : ' Compare recovered carats vs plan (left) and ore processed (right). If carat deviation is greater than ore deviation, the problem is in conversion (grade). If equal, the problem is in processed volume.'}
              </span>
            </p>
          </div>
          
          <p className="text-[10px] text-slate-400 mt-3 text-center">
            {language === 'pt' ? 'Fonte: Relatórios Mensais de Produção' : 'Source: Monthly Production Reports'}
          </p>
        </div>
      )}

      {/* ============ BLOCO 3: MIX OPERACIONAL COM IMPACTO COMERCIAL ============ */}
      {is2026 && data.mixOperacional && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Layers className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 text-sm">{language === 'pt' ? 'Mix Operacional com Impacto Comercial' : 'Operational Mix with Commercial Impact'}</h3>
              <p className="text-[10px] text-slate-500">{language === 'pt' ? 'O mix de produção ajuda a explicar o comportamento da qualidade e potencial de receita?' : 'Does production mix help explain quality behavior and revenue potential?'}</p>
            </div>
          </div>
          
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left py-2 px-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{language === 'pt' ? 'Origem' : 'Origin'}</th>
                  <th className="text-center py-2 px-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px] w-24">{language === 'pt' ? 'Peso no Mix' : 'Mix Weight'}</th>
                  <th className="text-center py-2 px-2 font-semibold text-slate-500 uppercase tracking-wider text-[10px] w-20">{language === 'pt' ? 'Qualidade' : 'Quality'}</th>
                  <th className="text-center py-2 px-2 font-semibold text-slate-500 uppercase tracking-wider text-[10px] w-28">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.mixOperacional.map((item, idx) => (
                  <tr key={idx} className={`border-t border-slate-100 ${idx % 2 === 0 ? 'bg-slate-50/30' : ''}`}>
                    <td className="py-2.5 px-3">
                      <span className="text-slate-700 font-medium">{item.origem}</span>
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <span className="font-bold text-slate-800">{item.percentagem}%</span>
                    </td>
                    <td className="py-2.5 px-2 text-center">
                      <QualityBadge quality={item.qualidade} language={language} />
                    </td>
                    <td className="py-2.5 px-2 text-center">
                      <StatusBadge status={item.status} language={language} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-slate-400 mt-2.5 flex items-center gap-1">
            <FileText className="w-3 h-3" />
            {language === 'pt' ? 'Fonte: Relatórios de Geologia / QA-QC e mapas de produção' : 'Source: Geology / QA-QC Reports and production maps'}
          </p>
        </div>
      )}

      {/* ============ BLOCO 4: PRINCIPAIS CONSTRANGIMENTOS DA PRODUÇÃO ============ */}
      {is2026 && data.constrangimentos && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 text-sm">{language === 'pt' ? 'Principais Constrangimentos da Produção' : 'Main Production Constraints'}</h3>
              <p className="text-[10px] text-slate-500">{language === 'pt' ? 'O que impediu a operação de produzir melhor valor no período?' : 'What prevented operations from producing better value in the period?'}</p>
            </div>
          </div>
          
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left py-2 px-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{language === 'pt' ? 'Facto Operacional' : 'Operational Fact'}</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{language === 'pt' ? 'Efeito na Produção' : 'Production Effect'}</th>
                  <th className="text-center py-2 px-2 font-semibold text-slate-500 uppercase tracking-wider text-[10px] w-20">{language === 'pt' ? 'Impacto' : 'Impact'}</th>
                </tr>
              </thead>
              <tbody>
                {data.constrangimentos.map((item, idx) => (
                  <tr key={idx} className={`border-t border-slate-100 ${idx % 2 === 0 ? 'bg-slate-50/30' : ''}`}>
                    <td className="py-2.5 px-3">
                      <span className="text-slate-700">{item.facto[language]}</span>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="text-slate-600">{item.efeito[language]}</span>
                    </td>
                    <td className="py-2.5 px-2 text-center">
                      <SeverityBadge severity={item.severidade} language={language} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-slate-400 mt-2.5 flex items-center gap-1">
            <FileText className="w-3 h-3" />
            {language === 'pt' ? 'Fonte: Relatórios Mensais de Produção e reuniões de acompanhamento operacional' : 'Source: Monthly Production Reports and operational follow-up meetings'}
          </p>
        </div>
      )}

      {/* ============ BLOCO 5: PONTE OPERAÇÃO → LEITURA FINANCEIRA (qualitativa) ============ */}
      <div className="bg-gradient-to-br from-[#0A192F] to-[#112240] rounded-xl p-5">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-lg bg-gold-500/20 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-gold-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white text-sm">{language === 'pt' ? 'Ponte Operação → Leitura Financeira' : 'Operations → Financial Reading Bridge'}</h3>
            <p className="text-[10px] text-slate-400">{language === 'pt' ? 'Tradução qualitativa dos factos operacionais em potencial de receita' : 'Qualitative translation of operational facts to revenue potential'}</p>
          </div>
        </div>
        
        <div className="overflow-hidden rounded-lg border border-white/10">
          <table className="w-full">
            <thead>
              <tr className="bg-white/5">
                <th className="text-left py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{language === 'pt' ? 'Facto Operacional' : 'Operational Fact'}</th>
                <th className="text-left py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{language === 'pt' ? 'Implicação Operacional' : 'Operational Implication'}</th>
                <th className="text-left py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{language === 'pt' ? 'Leitura Financeira' : 'Financial Reading'}</th>
                <th className="text-center py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider w-20">{language === 'pt' ? 'Impacto' : 'Impact'}</th>
              </tr>
            </thead>
            <tbody>
              {data.ponteFinanceira.map((item, idx) => (
                <tr key={idx} className={`border-t border-white/5 ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                  <td className="py-2.5 px-3">
                    <span className={`text-xs font-medium ${
                      item.severidade === 'low' ? 'text-emerald-400' :
                      item.severidade === 'medium' ? 'text-amber-400' :
                      'text-rose-400'
                    }`}>
                      {item.facto[language]}
                    </span>
                  </td>
                  <td className="py-2.5 px-3">
                    <span className="text-[11px] text-slate-300">{item.implicacaoOperacional[language]}</span>
                  </td>
                  <td className="py-2.5 px-3">
                    <span className="text-[11px] text-slate-300">{item.leituraFinanceira[language]}</span>
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                      item.severidade === 'low' ? 'bg-emerald-500/20 text-emerald-400' :
                      item.severidade === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-rose-500/20 text-rose-400'
                    }`}>
                      {item.severidade === 'low' ? (language === 'pt' ? 'Limitado' : 'Limited') :
                       item.severidade === 'medium' ? (language === 'pt' ? 'Moderado' : 'Moderate') :
                       (language === 'pt' ? 'Relevante' : 'Relevant')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-slate-500 mt-3 flex items-center gap-1">
          <FileText className="w-3 h-3" />
          {language === 'pt' ? 'Nota: Leitura qualitativa — quantificação financeira requer modelo de preços e análise dedicada' : 'Note: Qualitative reading — financial quantification requires pricing model and dedicated analysis'}
        </p>
      </div>
    </div>
  );
};

export default ProductionRevenue;
